import React, { useEffect, useRef, useState } from 'react';
import { getCastAvailabilityDetails, getCastState } from '../../javascript/cast/cast-utils';
import { requestCastSession, subscribeCastStateChanged } from '../../javascript/cast/cast-service';
import CastFallbackIcon from './cast-fallback-icon';
import style from './cast-button.module.scss';

const LOG = '[CastDebug] CastButton';

const isLauncherVisible = (launcher) => {
   if (!launcher) {
      return false;
   }

   const rect = launcher.getBoundingClientRect();
   const computedStyle = window.getComputedStyle(launcher);

   return (
      rect.width > 0 &&
      rect.height > 0 &&
      computedStyle.display !== 'none' &&
      computedStyle.visibility !== 'hidden'
   );
};

const logLauncherVisibility = (launcher, reason) => {
   const castState = getCastState();

   if (!launcher) {
      console.log(`${LOG} launcher icon: not in DOM (${reason})`, { castState });
      return false;
   }

   const rect = launcher.getBoundingClientRect();
   const computedStyle = window.getComputedStyle(launcher);
   const visible = isLauncherVisible(launcher);

   console.log(`${LOG} CastState:`, castState);
   console.log(`${LOG} launcher icon: ${visible ? 'VISIBLE' : 'HIDDEN'} (${reason})`, {
      display: computedStyle.display,
      visibility: computedStyle.visibility,
      opacity: computedStyle.opacity,
      width: rect.width,
      height: rect.height,
   });

   return visible;
};

const shouldShowFallback = (castState, launcherVisible) => {
   if (castState === null || castState === 'NO_DEVICES_AVAILABLE') {
      return true;
   }

   return !launcherVisible;
};

const handleFallbackClick = (event) => {
   event.stopPropagation();
   console.log(`${LOG} fallback clicked: requesting cast session`);
   requestCastSession();
};

function CastButton() {
   const [castAvailable, setCastAvailable] = useState(false);
   const [showFallback, setShowFallback] = useState(true);
   const wrapperRef = useRef(null);
   const prevAvailableRef = useRef(null);
   const prevFallbackRef = useRef(null);

   useEffect(() => {
      console.log(`${LOG} mounted`);

      const checkCastAvailability = (reason) => {
         const details = getCastAvailabilityDetails();
         const { available } = details;

         if (!available) {
            console.log(`${LOG} API check (${reason}): not ready`, details);
         } else {
            console.log(`${LOG} API check (${reason}): ready`, details);
         }

         setCastAvailable((prev) => {
            if (prev !== available) {
               console.log(`${LOG} component ${available ? 'SHOW' : 'HIDE'}: Cast API ${available ? 'available' : 'unavailable'}`);
            }
            return available;
         });

         return available;
      };

      const availableImmediately = checkCastAvailability('initial');
      if (availableImmediately) {
         return () => console.log(`${LOG} unmounted`);
      }

      console.log(`${LOG} polling for Cast API (every 1s)`);
      const interval = window.setInterval(() => {
         const available = checkCastAvailability('poll');
         if (available) {
            console.log(`${LOG} polling stopped: Cast API ready`);
            window.clearInterval(interval);
         }
      }, 1000);

      return () => {
         window.clearInterval(interval);
         console.log(`${LOG} unmounted`);
      };
   }, []);

   useEffect(() => {
      if (prevAvailableRef.current === castAvailable) {
         return undefined;
      }

      prevAvailableRef.current = castAvailable;
      console.log(`${LOG} render decision: ${castAvailable ? 'rendering launcher' : 'returning null (hidden)'}`);
   }, [castAvailable]);

   useEffect(() => {
      if (!castAvailable) {
         return undefined;
      }

      const updateFallback = (castState, reason = 'cast state') => {
         const launcher = wrapperRef.current?.querySelector('google-cast-launcher');
         const launcherVisible = logLauncherVisibility(launcher, reason);
         const nextShowFallback = shouldShowFallback(castState ?? getCastState(), launcherVisible);

         if (prevFallbackRef.current !== nextShowFallback) {
            console.log(`${LOG} fallback icon: ${nextShowFallback ? 'SHOW' : 'HIDE'} (${reason})`, {
               castState: castState ?? getCastState(),
               launcherVisible,
            });
            prevFallbackRef.current = nextShowFallback;
         }

         setShowFallback(nextShowFallback);
      };

      updateFallback(getCastState(), 'initial');

      const unsubscribe = subscribeCastStateChanged((castState) => {
         updateFallback(castState, 'cast state changed');
      });

      const observers = [];
      const launcher = wrapperRef.current?.querySelector('google-cast-launcher');

      if (launcher) {
         const mutationObserver = new MutationObserver(() => {
            updateFallback(getCastState(), 'DOM mutation');
         });
         mutationObserver.observe(launcher, {
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
            childList: true,
            subtree: true,
         });
         observers.push(() => mutationObserver.disconnect());

         if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
               updateFallback(getCastState(), 'size change');
            });
            resizeObserver.observe(launcher);
            observers.push(() => resizeObserver.disconnect());
         }
      }

      return () => {
         unsubscribe();
         observers.forEach((disconnect) => disconnect());
      };
   }, [castAvailable]);

   if (!castAvailable) {
      return null;
   }

   return (
      <div ref={wrapperRef} className={style.castButtonWrap}>
         {showFallback && (
            <div
               className={style.castFallbackWrap}
               onClick={handleFallbackClick}
               role="button"
               aria-label="Cast"
            >
               <CastFallbackIcon className="cast-fallback-icon" />
            </div>
         )}
         <google-cast-launcher
            style={showFallback ? { pointerEvents: 'none' } : undefined}
         />
      </div>
   );
}

export default CastButton;
